"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { Minus, Plus, X } from "lucide-react"
import { prepararAntispam, tokenAntispam } from "@/lib/antispam/cliente"
import { comprobarEntrega, costeDeEnvio, formasDisponibles, redondear, type EntregaDelVendedor, type FormaDeEntrega } from "@/lib/mercado/entrega"
import { nombreDeLaComunidad } from "@/lib/mercado/comunidades"
import { conAncho } from "@/lib/mercado/fotos"
import { rutaDelMercado } from "@/lib/mercado/rutas"
import { euros as eurosEs } from "@/lib/mercado/texto"
import type { Idioma } from "@/lib/landing/idioma"
import { alCambiarLaCesta, guardarCesta, leerCesta, vaciarCesta, type CestaGuardada } from "./cesta-local"
import { errorEn, textosMercado } from "./textos"

/**
 * La cesta y el paso a pagar.
 *
 * La cesta del navegador solo dice que y cuanto; lo demas se pide al servidor al
 * abrirla. Los importes que se ven aqui se calculan con las mismas funciones que
 * usa el servidor para cobrar (lib/mercado/entrega.ts), pero el que vale es el
 * del servidor: esto es solo para que el comprador sepa lo que va a pagar antes
 * de irse a Stripe.
 */

interface VendedorDeLaCesta {
  id: string
  slug: string
  nombre: string
  municipio: string
  municipioSlug: string
  islaSlug: string | null
  direccion: string | null
  codigoPostal: string | null
  comunidad: string | null
  envioPlazo: string | null
  recogidaInstrucciones: string | null
  entrega: EntregaDelVendedor
  vende: boolean
}

interface LineaDeLaCesta {
  varianteId: string
  titulo: string
  variante: string | null
  imagen: string | null
  slug: string
  precio: number
  disponible: number
}

const CAMPO = "w-full rounded-xl border border-zinc-300 px-4 py-3 text-sm text-zinc-950 outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-950"
const ETIQUETA = "mb-1.5 block text-sm font-medium text-zinc-950"

export function Cesta({ idioma = "es" }: { idioma?: Idioma }) {
  const t = textosMercado(idioma)
  const euros = (importe: number | null | undefined) => eurosEs(importe, idioma)
  const [cesta, setCesta] = useState<CestaGuardada | null>(null)
  const [vendedor, setVendedor] = useState<VendedorDeLaCesta | null>(null)
  const [lineas, setLineas] = useState<LineaDeLaCesta[]>([])
  const [cargando, setCargando] = useState(true)
  const [fallo, setFallo] = useState<string | null>(null)

  const [forma, setForma] = useState<FormaDeEntrega | null>(null)
  const [nombre, setNombre] = useState("")
  const [email, setEmail] = useState("")
  const [telefono, setTelefono] = useState("")
  const [direccion, setDireccion] = useState("")
  const [extra, setExtra] = useState("")
  const [codigoPostal, setCodigoPostal] = useState("")
  const [ciudad, setCiudad] = useState("")
  const [notas, setNotas] = useState("")
  const [trampa, setTrampa] = useState("")
  const [pagando, setPagando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agotados, setAgotados] = useState<string[]>([])

  // La cesta guardada, y sus cambios desde otra pestaña.
  useEffect(() => {
    const leer = () => setCesta(leerCesta())
    leer()
    return alCambiarLaCesta(leer)
  }, [])

  const ids = useMemo(() => (cesta?.lineas ?? []).map((l) => l.varianteId).sort().join(","), [cesta])

  // Lo que hay hoy de cada cosa: se pide cuando cambia QUE hay en la cesta, no cuanto.
  const cargar = useCallback(async (variantes: string[]) => {
    if (!variantes.length) {
      setVendedor(null)
      setLineas([])
      setCargando(false)
      return
    }
    setCargando(true)
    setFallo(null)
    try {
      const respuesta = await fetch("/api/public/mercado/cesta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variantes }),
      })
      const datos = await respuesta.json()
      if (!respuesta.ok) throw new Error(errorEn(idioma, datos?.error, t.noSeHaPodidoCargar))
      setVendedor(datos.vendedor ?? null)
      setLineas(datos.lineas ?? [])
    } catch (e) {
      setFallo(e instanceof Error ? e.message : t.noSeHaPodidoCargar)
    } finally {
      setCargando(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cesta === null) return
    cargar(ids ? ids.split(",") : [])
  }, [ids, cesta === null, cargar]) // eslint-disable-line react-hooks/exhaustive-deps

  const formas = useMemo(() => (vendedor ? formasDisponibles(vendedor.entrega) : []), [vendedor])
  useEffect(() => {
    if (formas.length && (!forma || !formas.includes(forma))) setForma(formas[0])
  }, [formas, forma])

  const cantidadDe = (varianteId: string) => cesta?.lineas.find((l) => l.varianteId === varianteId)?.cantidad ?? 0

  function cambiarCantidad(varianteId: string, cantidad: number) {
    if (!cesta) return
    const lineasNuevas = cesta.lineas
      .map((l) => (l.varianteId === varianteId ? { ...l, cantidad } : l))
      .filter((l) => l.cantidad > 0)
    guardarCesta({ ...cesta, lineas: lineasNuevas })
    setAgotados((antes) => antes.filter((id) => id !== varianteId))
    setError(null)
  }

  const subtotal = redondear(lineas.reduce((suma, l) => suma + l.precio * Math.min(cantidadDe(l.varianteId), Math.max(l.disponible, 0)), 0))
  const envio = vendedor && forma ? costeDeEnvio(vendedor.entrega, forma, subtotal) : 0
  const total = redondear(subtotal + envio)
  const hayQueAjustar = lineas.some((l) => l.disponible <= 0 || cantidadDe(l.varianteId) > l.disponible)
  const destino = vendedor && forma === "envio" && codigoPostal.replace(/\s/g, "").length === 5
    ? comprobarEntrega(vendedor.entrega, "envio", codigoPostal, idioma)
    : null

  async function pagar(evento: React.FormEvent) {
    evento.preventDefault()
    if (!vendedor || !forma || !cesta) return
    setError(null)

    if (hayQueAjustar) {
      setError(t.ajustaAntesDePagar)
      return
    }
    if (forma === "envio") {
      const comprobacion = comprobarEntrega(vendedor.entrega, "envio", codigoPostal, idioma)
      if (!comprobacion.ok) {
        setError(comprobacion.motivo)
        return
      }
    }

    setPagando(true)
    try {
      const token = await tokenAntispam("compra")
      const respuesta = await fetch("/api/public/mercado/pedidos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vendedorId: vendedor.id,
          lineas: cesta.lineas,
          entrega: forma,
          comprador: { nombre, email, telefono },
          direccion: { linea: direccion, extra, codigoPostal, ciudad },
          notas,
          token,
          sitio_web: trampa,
        }),
      })
      const datos = await respuesta.json().catch(() => ({}))
      if (!respuesta.ok || !datos.url) {
        setAgotados(Array.isArray(datos.agotados) ? datos.agotados : [])
        setError(errorEn(idioma, datos.error, t.noSeHaPodidoIniciar))
        setPagando(false)
        // Lo que ha fallado suele ser que algo se ha vendido: se vuelve a pedir lo que hay.
        if (respuesta.status === 409) cargar(cesta.lineas.map((l) => l.varianteId))
        return
      }
      window.location.href = datos.url
    } catch {
      setError(t.revisaLaConexion)
      setPagando(false)
    }
  }

  if (cesta === null || cargando) {
    return <p className="text-zinc-500">{t.cargandoCesta}</p>
  }

  if (fallo) {
    return (
      <div>
        <p className="text-zinc-700">{fallo}</p>
        <button
          type="button"
          onClick={() => cargar(cesta.lineas.map((l) => l.varianteId))}
          className="mt-4 rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-medium text-zinc-900 hover:border-zinc-500"
        >
          {t.reintentar}
        </button>
      </div>
    )
  }

  if (!vendedor || !lineas.length) {
    return (
      <div>
        <p className="text-lg text-zinc-700">{t.cestaVacia}</p>
        {cesta.lineas.length > 0 && (
          <p className="mt-2 text-sm text-zinc-500">{t.yaNoEstaALaVenta}</p>
        )}
        <Link
          href={rutaDelMercado(idioma)}
          onClick={() => cesta.lineas.length > 0 && vaciarCesta()}
          className="mt-6 inline-flex rounded-full bg-zinc-950 px-6 py-3 text-sm font-medium text-white hover:bg-zinc-800"
        >
          {t.irAlMercado}
        </Link>
      </div>
    )
  }

  const rutaDelVendedor = rutaDelMercado(idioma, vendedor.islaSlug ?? "mallorca", vendedor.municipioSlug, vendedor.slug)
  const comunidad = nombreDeLaComunidad(vendedor.comunidad, idioma)

  return (
    <form onSubmit={pagar} onFocus={prepararAntispam} className="grid gap-12 lg:grid-cols-[1.3fr_1fr] lg:gap-16">
      <div>
        <p className="text-sm text-zinc-500">
          {t.vendidoPor}{" "}
          <Link href={rutaDelVendedor} className="font-medium text-zinc-950 underline underline-offset-4">
            {vendedor.nombre}
          </Link>
          , {vendedor.municipio}
        </p>

        <ul className="mt-6 divide-y divide-zinc-100 border-y border-zinc-100">
          {lineas.map((linea) => {
            const cantidad = cantidadDe(linea.varianteId)
            const sinStock = linea.disponible <= 0
            const sobra = !sinStock && cantidad > linea.disponible
            return (
              <li key={linea.varianteId} className="flex gap-4 py-5">
                <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
                  {linea.imagen && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={conAncho(linea.imagen, 200)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link href={`${rutaDelVendedor}/${linea.slug}`} className="font-medium text-zinc-950 hover:underline">
                        {linea.titulo}
                      </Link>
                      {linea.variante && <p className="text-sm text-zinc-500">{linea.variante}</p>}
                    </div>
                    <button
                      type="button"
                      aria-label={t.quitar(linea.titulo)}
                      onClick={() => cambiarCantidad(linea.varianteId, 0)}
                      className="p-1 text-zinc-400 hover:text-zinc-900"
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    {sinStock ? (
                      <span className="text-sm text-zinc-500">{t.agotado}</span>
                    ) : (
                      <div className="inline-flex items-center rounded-full border border-zinc-300">
                        <button
                          type="button"
                          aria-label={t.quitarUno}
                          className="p-2 text-zinc-700"
                          onClick={() => cambiarCantidad(linea.varianteId, cantidad - 1)}
                        >
                          <Minus className="h-3.5 w-3.5" aria-hidden />
                        </button>
                        <span className="w-7 text-center text-sm tabular-nums">{cantidad}</span>
                        <button
                          type="button"
                          aria-label={t.anadirUno}
                          className="p-2 text-zinc-700 disabled:opacity-30"
                          disabled={cantidad >= Math.min(50, linea.disponible)}
                          onClick={() => cambiarCantidad(linea.varianteId, cantidad + 1)}
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </div>
                    )}
                    <span className="text-sm tabular-nums text-zinc-900">{euros(linea.precio * cantidad)}</span>
                  </div>
                  {(sinStock || sobra || agotados.includes(linea.varianteId)) && (
                    <p className="mt-2 text-sm text-zinc-700">
                      {sinStock
                        ? t.yaNoQueda
                        : sobra
                          ? t.soloQuedan(linea.disponible)
                          : t.noQuedaSuficiente}
                      {sobra && (
                        <button
                          type="button"
                          className="font-medium text-zinc-950 underline underline-offset-4"
                          onClick={() => cambiarCantidad(linea.varianteId, linea.disponible)}
                        >
                          {t.dejar(linea.disponible)}
                        </button>
                      )}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ul>

        <fieldset className="mt-10">
          <legend className="text-lg font-medium text-zinc-950">{t.comoLoQuieres}</legend>
          {formas.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-600">{t.sinFormaDeEntrega}</p>
          ) : (
            <div className="mt-4 grid gap-3">
              {formas.map((opcion) => {
                const activa = forma === opcion
                const coste = costeDeEnvio(vendedor.entrega, opcion, subtotal)
                return (
                  <label
                    key={opcion}
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${
                      activa ? "border-zinc-950" : "border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    <input
                      type="radio"
                      name="entrega"
                      className="mt-1 accent-zinc-950"
                      checked={activa}
                      onChange={() => {
                        setForma(opcion)
                        setError(null)
                      }}
                    />
                    <span className="flex-1 text-sm">
                      <span className="flex items-baseline justify-between gap-3 font-medium text-zinc-950">
                        {opcion === "envio" ? t.envioADomicilioOpcion : t.recogidaEnElLocalOpcion}
                        <span className="tabular-nums">{coste > 0 ? euros(coste) : t.gratisMayuscula}</span>
                      </span>
                      <span className="mt-1 block text-zinc-600">
                        {opcion === "envio"
                          ? `${t.soloDentroDeOpcion(comunidad)}${vendedor.envioPlazo ? ` ${vendedor.envioPlazo}.` : ""}`
                          : `${[vendedor.direccion, [vendedor.codigoPostal, vendedor.municipio].filter(Boolean).join(" ")].filter(Boolean).join(", ")}.${vendedor.recogidaInstrucciones ? ` ${vendedor.recogidaInstrucciones}` : ""}`}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          )}
        </fieldset>

        <fieldset className="mt-10">
          <legend className="text-lg font-medium text-zinc-950">{t.tusDatos}</legend>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={ETIQUETA} htmlFor="cesta-nombre">{t.nombreYApellidos}</label>
              <input id="cesta-nombre" className={CAMPO} value={nombre} onChange={(e) => setNombre(e.target.value)} required maxLength={120} autoComplete="name" />
            </div>
            <div>
              <label className={ETIQUETA} htmlFor="cesta-email">{t.correo}</label>
              <input id="cesta-email" type="email" className={CAMPO} value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={200} autoComplete="email" />
            </div>
            <div>
              <label className={ETIQUETA} htmlFor="cesta-telefono">{t.telefono}{forma === "recogida" ? t.opcional : ""}</label>
              <input id="cesta-telefono" type="tel" className={CAMPO} value={telefono} onChange={(e) => setTelefono(e.target.value)} required={forma === "envio"} maxLength={30} autoComplete="tel" />
            </div>

            {forma === "envio" && (
              <>
                <div className="sm:col-span-2">
                  <label className={ETIQUETA} htmlFor="cesta-direccion">{t.direccionDeEntrega}</label>
                  <input id="cesta-direccion" className={CAMPO} value={direccion} onChange={(e) => setDireccion(e.target.value)} required maxLength={200} autoComplete="address-line1" placeholder={t.calleYNumero} />
                </div>
                <div className="sm:col-span-2">
                  <label className={ETIQUETA} htmlFor="cesta-extra">{t.pisoPuerta}</label>
                  <input id="cesta-extra" className={CAMPO} value={extra} onChange={(e) => setExtra(e.target.value)} maxLength={120} autoComplete="address-line2" />
                </div>
                <div>
                  <label className={ETIQUETA} htmlFor="cesta-cp">{t.codigoPostal}</label>
                  <input id="cesta-cp" className={CAMPO} value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} required inputMode="numeric" maxLength={5} autoComplete="postal-code" />
                  {destino && !destino.ok && <p className="mt-1.5 text-sm text-zinc-700">{destino.motivo}</p>}
                </div>
                <div>
                  <label className={ETIQUETA} htmlFor="cesta-ciudad">{t.poblacion}</label>
                  <input id="cesta-ciudad" className={CAMPO} value={ciudad} onChange={(e) => setCiudad(e.target.value)} required maxLength={100} autoComplete="address-level2" />
                </div>
              </>
            )}

            <div className="sm:col-span-2">
              <label className={ETIQUETA} htmlFor="cesta-notas">{t.notaParaElVendedor}</label>
              <textarea id="cesta-notas" className={CAMPO} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} maxLength={500} />
            </div>

            {/* Campo trampa: una persona no lo ve ni lo rellena. */}
            <div className="hidden" aria-hidden>
              <label htmlFor="cesta-sitio-web">Sitio web</label>
              <input id="cesta-sitio-web" name="sitio_web" tabIndex={-1} autoComplete="off" value={trampa} onChange={(e) => setTrampa(e.target.value)} />
            </div>
          </div>
        </fieldset>
      </div>

      <aside className="h-fit rounded-2xl border border-zinc-200 p-6 lg:sticky lg:top-28">
        <h2 className="text-lg font-medium text-zinc-950">{t.resumen}</h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-zinc-600">{t.productosResumen}</dt>
            <dd className="tabular-nums text-zinc-950">{euros(subtotal)}</dd>
          </div>
          {forma === "envio" && (
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-600">{t.envio}</dt>
              <dd className="tabular-nums text-zinc-950">{envio > 0 ? euros(envio) : t.gratisMayuscula}</dd>
            </div>
          )}
          {forma === "envio" && envio > 0 && vendedor.entrega.envioGratisDesde !== null && vendedor.entrega.envioGratisDesde > subtotal && (
            <p className="text-xs text-zinc-500">
              {t.conMasEnvioGratis(euros(redondear(vendedor.entrega.envioGratisDesde - subtotal)))}
            </p>
          )}
          <div className="flex justify-between gap-4 border-t border-zinc-100 pt-3 text-base font-medium">
            <dt className="text-zinc-950">{t.total}</dt>
            <dd className="tabular-nums text-zinc-950">{euros(total)}</dd>
          </div>
        </dl>

        {error && (
          <p className="mt-4 rounded-xl border border-zinc-300 p-3 text-sm text-zinc-800" role="alert">
            {error}
          </p>
        )}

        {vendedor.vende ? (
          <button
            type="submit"
            disabled={pagando || formas.length === 0 || subtotal <= 0}
            className="mt-5 w-full rounded-full bg-zinc-950 px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-50"
          >
            {pagando ? t.abriendoElPago : t.pagar(euros(total))}
          </button>
        ) : (
          <p className="mt-5 text-sm text-zinc-600">
            {t.cestaGuardada(vendedor.nombre)}
          </p>
        )}
        <p className="mt-3 text-xs leading-relaxed text-zinc-500">
          {t.pagoSeguro(vendedor.nombre)}
        </p>
      </aside>
    </form>
  )
}
